/* eslint-disable solid/no-innerhtml */

import Fuse from 'fuse.js'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
} from 'solid-js'
import { render } from 'solid-js/web'
import {
  debounce,
  getSelectedOptions,
  getUrlState,
  isEmptyObject,
  matchesMediaQuery,
  setUrlState,
} from '../utilities'
import { FOCUSABLE_SELECTORS } from '../utilities/constants'

// function normalizeString(str = '') {
//   return str.normalize('NFD').replace(/[\u0300-\u036F]/g, '')
// }

// const FILTER_AND = 'AND'
const FILTER_OR = 'OR'

export default class {
  constructor(el) {
    const dataEl = el.querySelector('.js-archiveData')
    const listEl = el.querySelector('.js-archiveList')

    if (!dataEl || !listEl) {
      return
    }

    const formEl = el.querySelector('.js-archiveForm')
    const searchEl = formEl.querySelector('.js-archiveSearch')
    const resetSearchButton = formEl.querySelector('.js-archiveResetSearch')
    const filterEls =
      formEl && Array.from(formEl.querySelectorAll('.js-archiveFilter'))
    const scoreFilterEl = filterEls?.find((filterEl) => filterEl.dataset.score)
    const resetButton = el.querySelector('.js-archiveReset')

    const AVAILABLE_SCORES = Array.from({
      length: scoreFilterEl?.options?.length ?? 0,
    })
      .map((_, i) => Math.floor((i + 1) * (i + 1)))
      .reverse()

    const defaultFilters = {}
    const filterTypes = {}

    if (filterEls) {
      filterEls.forEach((filterEl) => {
        const filterName = filterEl.getAttribute('name')
        const filterType = filterEl.getAttribute('data-type')
        const filterValue = getSelectedOptions(filterEl).map(
          (option) => option.value,
        )

        defaultFilters[filterName] = filterValue
        filterTypes[filterName] = filterType
      })
    }

    const data = JSON.parse(dataEl.textContent)

    if (!data.items.length) {
      throw new Error('No items found in data')
    }

    const fuse = new Fuse(data.items, {
      threshold: 0.3,
      keys: [
        {
          name: 'title',
          weight: 1,
          // getFn: (item) => normalizeString(item.title),
        },
        {
          name: 'locations',
          weight: 0.2,
        },
        {
          name: 'disciplines',
          weight: 0.1,
        },
        // {
        //   name: 'description',
        //   weight: 0.05,
        //   getFn: (item) =>
        //     item?.description ? JSON.stringify(item.description) : null,
        // },
      ],
    })

    render(() => {
      let urlState = getUrlState()

      if (urlState && !isEmptyObject(urlState)) {
        for (let urlKey in urlState) {
          if (!(urlKey in defaultFilters)) {
            delete urlState[urlKey]
            continue
          }

          if (!Array.isArray(urlState[urlKey]) || !urlState[urlKey].length) {
            urlState[urlKey] = defaultFilters[urlKey]
          }
        }
      }

      const [searchValue, setSearchValue] = createSignal(searchEl?.value ?? '')
      const [filterValue, setFilterValue] = createSignal(urlState)

      function handleSearch(e) {
        setSearchValue(e.target.value)
      }

      onMount(() => {
        if (formEl) {
          formEl.addEventListener('submit', (e) => {
            e.preventDefault()

            const firstFocusable = listEl.querySelector(FOCUSABLE_SELECTORS)

            if (firstFocusable) {
              firstFocusable.focus({
                focusVisible: false,
              })
            } else {
              listEl.focus()
            }

            return false
          })

          if (resetButton) {
            resetButton.addEventListener('click', () => {
              formEl.reset()

              if (searchEl) {
                searchEl.value = ''
                setSearchValue('')
              }
            })
          }

          if (searchEl && resetSearchButton) {
            resetSearchButton.addEventListener('click', () => {
              searchEl.value = ''
              setSearchValue('')
            })
          }
        }

        const onSelectMutation = () => {
          setFilterValue(() => {
            let newValue = {}

            filterEls?.forEach((filterEl) => {
              const filterName = filterEl.getAttribute('name')
              const filterValue = getSelectedOptions(filterEl).map(
                (option) => option.value,
              )

              newValue[filterName] = filterValue
            })

            return newValue
          })
        }

        const observer = new MutationObserver(onSelectMutation)

        if (filterEls) {
          filterEls.forEach((filterEl) => {
            if (filterEl) {
              observer.observe(filterEl, {
                attributes: true,
                childList: false,
                subtree: true,
              })
            }
          })
        }

        if (searchEl) {
          searchEl.addEventListener('input', debounce(handleSearch, 200))

          const isTouch = matchesMediaQuery('(pointer: coarse)')
          if (isTouch) {
            searchEl.addEventListener(
              'keyup',
              (e) => e.key === 'Enter' && searchEl.blur(),
            )
          }
        }
      })

      createEffect(() => {
        let newFilter = {}

        if (searchValue()) {
          newFilter.search = searchValue()
        }

        Object.keys(filterValue()).forEach((filterKey) => {
          const filterVal = filterValue()[filterKey]

          if (filterVal && newFilter[filterKey] !== filterVal) {
            newFilter[filterKey] = filterVal
          }
        })

        setUrlState(newFilter)
      })

      return (
        <ArchiveList
          searchValue={searchValue}
          filterValue={filterValue}
          filterTypes={filterTypes}
          fuse={fuse}
          locationGroups={data.location_groups}
          scores={AVAILABLE_SCORES}
        />
      )
    }, listEl)
  }
}

const ArchiveList = (props) => {
  const resultCountEl = document.body.querySelector('.js-archiveResultCount')

  const filteredItems = createMemo(() => {
    const searchValue = props.searchValue()
    const filterValue = props.filterValue()

    const scores = props.scores
    const filterTypes = props.filterTypes
    const locationGroups = props.locationGroups

    const items = searchValue.length
      ? props.fuse.search(searchValue).map((fuseItem) => fuseItem.item)
      : props.fuse._docs

    const result = items
      .reduce(
        (acc, item) => {
          const matchAll = Object.keys(filterValue).every((filterKey) => {
            if (filterValue[filterKey].length) {
              if (filterTypes[filterKey] === FILTER_OR) {
                return filterValue[filterKey].some((value) => {
                  if (!item[filterKey]) {
                    return
                  }

                  return item[filterKey].some((itemFilterValue) =>
                    itemFilterValue.includes(value),
                  )
                })
              } else {
                return filterValue[filterKey].every((value) =>
                  (Array.isArray(item[filterKey])
                    ? item[filterKey][0]
                    : item[filterKey]
                  )?.includes(value),
                )
              }
            }

            return true
          })

          if (matchAll) {
            acc[0].push(item)
          } else {
            const matchAllButLocationButIsInGroup =
              locationGroups &&
              item.locations?.length &&
              Object.keys(filterValue).every((filterKey) => {
                if (filterKey === 'locations') {
                  return filterValue[filterKey].some((filterLocation) =>
                    locationGroups.some(
                      (group) =>
                        group.includes(filterLocation) &&
                        item.locations.some((location) =>
                          group.includes(
                            Array.isArray(location) ? location[0] : location,
                          ),
                        ),
                    ),
                  )
                }

                if (filterValue[filterKey].length) {
                  if (filterTypes[filterKey] === FILTER_OR) {
                    return filterValue[filterKey].some((value) =>
                      (Array.isArray(item[filterKey])
                        ? item[filterKey][0]
                        : item[filterKey]
                      )?.includes(value),
                    )
                  } else {
                    return filterValue[filterKey].every((value) =>
                      (Array.isArray(item[filterKey])
                        ? item[filterKey][0]
                        : item[filterKey]
                      )?.includes(value),
                    )
                  }
                }

                return true
              })

            if (matchAllButLocationButIsInGroup) {
              acc[1].push(item)
            }
          }

          return acc
        },
        [[], []],
      )
      .map((list) =>
        list
          .map((item) => ({
            ...item,
            score:
              scores?.length && filterValue.disciplines?.length
                ? filterValue.disciplines.reduce((acc, filterDiscipline) => {
                    const disciplineIndex = item.disciplines.findIndex(
                      ([
                        itemDisicplineSlug,
                        // itemDisicplineName
                      ]) => itemDisicplineSlug === filterDiscipline,
                    )

                    if (disciplineIndex !== -1) {
                      acc += scores[disciplineIndex]
                    }

                    return acc
                  }, 0)
                : 0,
          }))
          .sort((a, b) => b.score - a.score),
      )

    return result
  })

  createEffect((prev) => {
    let itemCount = filteredItems()[0]?.length

    if (!resultCountEl || itemCount === prev) {
      return prev
    }

    resultCountEl.textContent = itemCount ?? '0'

    return itemCount
  }, 0)

  return (
    <>
      <ul class="Archive-list">
        <Show
          when={filteredItems()[0]?.length}
          fallback={
            <li class="Archive-item Archive-item-empty">
              Hittade inga medlemmar
            </li>
          }
        >
          <For each={filteredItems()[0]}>
            {(item) => <ArchiveItem {...item} />}
          </For>
        </Show>
      </ul>
      <Show when={filteredItems()[1]?.length}>
        <p class="Archive-resultTitle">
          {filteredItems()[0].length ? 'Fler byråer' : 'Byråer'} i närheten
        </p>
        <ul class="Archive-list">
          <For each={filteredItems()[1]}>
            {(item) => <ArchiveItem {...item} />}
          </For>
        </ul>
      </Show>
    </>
  )
}

const ArchiveItem = (props) => {
  return (
    <li class="Archive-item">
      <a class="Archive-itemInner" href={props.url}>
        <div class="Archive-itemContent">
          <p class="Archive-itemTitle" innerHTML={props.title} />
          <Show when={props?.disciplines?.length || props?.locations?.length}>
            <p
              class="Archive-itemMeta"
              innerHTML={
                (props?.disciplines?.length
                  ? props.disciplines
                      .slice(0, 3)
                      .reduce((acc, discipline, i, arr) => {
                        acc += discipline[1]

                        if (i < arr.length - 1) {
                          acc += ' / '
                        }

                        return acc
                      }, '')
                  : '') +
                (props?.disciplines?.length && props?.locations?.length
                  ? ' – '
                  : '') +
                (props?.locations?.length
                  ? `<span class="u-textNowrap">${props.locations[0][1]}${
                      props.locations.length > 1 ? ' m.fl.' : ''
                    }</span>`
                  : '')
              }
            />
          </Show>
        </div>
        <svg
          class="Archive-itemSymbol"
          width="18"
          height="18"
          aria-hidden="true"
        >
          <use xlink:href="#symbol-arrow-right" />
        </svg>
      </a>
    </li>
  )
}
